import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  MapContainer, TileLayer, Polyline, Polygon, CircleMarker, Tooltip, useMapEvents, useMap
} from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import './GeospatialConfigurator.css';
import { getAccessToken } from '../utils/auth';
import {
  getGeospatialFeatures, saveGeospatialFeature, deleteGeospatialFeature
} from '../utils/httpApi';
import { safeParseGeoJson, toLatLngs, buildGeometry } from '../utils/geoJson';

/**
 * Geospatial configurator (admin) — curates the AvioTrack Billing geospatial
 * model. The map opens with the airport's known features (seeded from OSM) as
 * an overlay; the panel on the right lists every feature with edit / move /
 * delete, and the draw tools add new ones. Drawing is plain Leaflet click
 * handling — no extra dependency. Geometry crosses the API as GeoJSON.
 */

const AIRPORT = { icao: 'EIDW', name: 'Dublin', center: [53.4264, -6.2499], zoom: 14 };

const TYPE_COLOR = {
  RUNWAY: '#e74c3c', TAXIWAY: '#95a5a6', APRON: '#3498db',
  STAND: '#FF6B35', TERMINAL_ZONE: '#9b59b6', AERODROME_ZONE: '#16a085'
};
const FEATURE_TYPES = Object.keys(TYPE_COLOR);

// Draw mode -> the feature type it creates and its GeoJSON geometry kind.
const MODES = {
  runway:   { featureType: 'RUNWAY',        geom: 'LineString', label: 'Runway (line)' },
  taxiway:  { featureType: 'TAXIWAY',       geom: 'LineString', label: 'Taxiway (line)' },
  stand:    { featureType: 'STAND',         geom: 'Point',      label: 'Stand / gate (dot)' },
  parking:  { featureType: 'APRON',         geom: 'Polygon',    label: 'Parking area (polygon)' },
  terminal: { featureType: 'TERMINAL_ZONE', geom: 'Polygon',    label: 'Terminal zone (polygon)' }
};
const TYPE_TO_MODE = {
  RUNWAY: 'runway', TAXIWAY: 'taxiway', STAND: 'stand',
  APRON: 'parking', TERMINAL_ZONE: 'terminal', AERODROME_ZONE: 'terminal'
};

const STAND_TYPES = ['', 'CONTACT', 'REMOTE', 'SATELLITE'];
const ZONES = ['', 'T1', 'T2', 'WEST_AERODROME'];

function apiOk(resp) {
  const s = resp && resp.header && resp.header.apiStatus;
  return s === 'OK' || s === 'SUCCESS';
}

function apiError(resp) {
  const errs = resp && resp.header && resp.header.errors;
  if (errs && errs.length) return errs[0].description || errs[0].message || 'error';
  return 'request failed';
}

/** A feature's representative point — for panning the map to it. */
function focusOf(parsed) {
  if (!parsed) return null;
  if (parsed.kind === 'Point') return parsed.latlngs;
  const pts = parsed.latlngs;
  if (!pts || !pts.length) return null;
  const la = pts.reduce((s, p) => s + p[0], 0) / pts.length;
  const lo = pts.reduce((s, p) => s + p[1], 0) / pts.length;
  return [la, lo];
}

/** Captures map clicks while a draw mode is active. */
function DrawHandler({ active, onPoint }) {
  useMapEvents({
    click(e) { if (active) onPoint([e.latlng.lat, e.latlng.lng]); }
  });
  return null;
}

/** Pans the map when the focus target changes. */
function MapFocus({ target }) {
  const map = useMap();
  useEffect(() => {
    if (target) map.panTo(target);
  }, [target, map]);
  return null;
}

function GeospatialConfigurator() {
  const [features, setFeatures] = useState([]);
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState(null);
  const [mode, setMode] = useState('select');
  const [draft, setDraft] = useState([]);
  const [redrawId, setRedrawId] = useState(null);
  const [selectedId, setSelectedId] = useState(null);
  const [listFilter, setListFilter] = useState('STAND');

  // New-feature form.
  const [formCode, setFormCode] = useState('');
  const [formStandType, setFormStandType] = useState('');
  const [formZone, setFormZone] = useState('');
  // Selected-feature edit form.
  const [editCode, setEditCode] = useState('');
  const [editStandType, setEditStandType] = useState('');
  const [editZone, setEditZone] = useState('');

  const loadFeatures = useCallback(() => {
    const token = getAccessToken();
    if (!token) { setStatus('Not authenticated.'); return; }
    setLoading(true);
    getGeospatialFeatures(AIRPORT.icao, null, token)
      .then((resp) => {
        if (!apiOk(resp)) { setStatus('Load failed: ' + apiError(resp)); return; }
        const parsed = (resp.features || []).map((f) => ({
          ...f, parsed: toLatLngs(safeParseGeoJson(f.geometry))
        }));
        setFeatures(parsed);
        setStatus(parsed.length === 0
          ? 'No features for ' + AIRPORT.icao + ' — has the billing schema been '
            + 'loaded (migrations V043+) and the backend deployed?'
          : 'Loaded ' + parsed.length + ' feature(s).');
      })
      .catch((e) => setStatus('Load error: ' + e.message))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { loadFeatures(); }, [loadFeatures]);

  const selected = useMemo(
    () => features.find((f) => f.id === selectedId) || null,
    [features, selectedId]
  );

  useEffect(() => {
    setEditCode(selected ? (selected.featureCode || '') : '');
    setEditStandType(selected ? (selected.standType || '') : '');
    setEditZone(selected ? (selected.zone || '') : '');
  }, [selected]);

  const counts = useMemo(() => {
    const c = {};
    features.forEach((f) => { c[f.featureType] = (c[f.featureType] || 0) + 1; });
    return c;
  }, [features]);

  const listed = useMemo(
    () => features
      .filter((f) => f.featureType === listFilter)
      .sort((a, b) => (a.featureCode || '').localeCompare(b.featureCode || '')),
    [features, listFilter]
  );

  const focusTarget = useMemo(
    () => (selected ? focusOf(selected.parsed) : null),
    [selected]
  );

  function resetDraw() {
    setDraft([]);
    setRedrawId(null);
    setMode('select');
  }

  function startAdd(m) {
    setMode(m);
    setDraft([]);
    setRedrawId(null);
    setSelectedId(null);
    setStatus('Drawing a new ' + MODES[m].featureType + '.');
  }

  function startRedraw() {
    if (!selected) return;
    const m = TYPE_TO_MODE[selected.featureType];
    if (!m) { setStatus('This feature type cannot be redrawn here.'); return; }
    setRedrawId(selected.id);
    setMode(m);
    setDraft([]);
    setStatus('Move / redraw ' + selected.featureType + ' ' + (selected.featureCode || '')
      + ' — draw the new geometry.');
  }

  function addPoint(latlng) {
    const cfg = MODES[mode];
    if (!cfg) return;
    setDraft((prev) => (cfg.geom === 'Point' ? [latlng] : [...prev, latlng]));
  }

  function saveDraft() {
    const cfg = MODES[mode];
    const geometry = buildGeometry(cfg.geom, draft);
    if (!geometry) {
      setStatus('Add more points — a ' + cfg.geom + ' needs '
        + (cfg.geom === 'Point' ? '1' : cfg.geom === 'LineString' ? '2+' : '3+') + '.');
      return;
    }
    const token = getAccessToken();
    let payload;
    if (redrawId != null) {
      const f = features.find((x) => x.id === redrawId);
      if (!f) { setStatus('Feature to redraw is no longer loaded.'); return; }
      payload = {
        airportIcao: AIRPORT.icao, id: redrawId, featureType: f.featureType,
        featureCode: f.featureCode, standType: f.standType, zone: f.zone,
        geometry: JSON.stringify(geometry)
      };
    } else {
      payload = {
        airportIcao: AIRPORT.icao, featureType: cfg.featureType,
        featureCode: formCode.trim() || null,
        standType: cfg.featureType === 'STAND' ? (formStandType || null) : null,
        zone: formZone || null, geometry: JSON.stringify(geometry)
      };
    }
    setStatus('Saving…');
    saveGeospatialFeature(payload, token)
      .then((resp) => {
        if (!apiOk(resp)) { setStatus('Save failed: ' + apiError(resp)); return; }
        setStatus(redrawId != null ? 'Geometry updated.' : 'Feature added.');
        setFormCode(''); setFormStandType(''); setFormZone('');
        resetDraw();
        loadFeatures();
      })
      .catch((e) => setStatus('Save error: ' + e.message));
  }

  function saveSelectedEdits() {
    if (!selected) return;
    const token = getAccessToken();
    const payload = {
      airportIcao: AIRPORT.icao, id: selected.id, featureType: selected.featureType,
      featureCode: editCode.trim() || null,
      standType: selected.featureType === 'STAND' ? (editStandType || null) : null,
      zone: editZone || null, geometry: selected.geometry // geometry unchanged
    };
    setStatus('Saving changes…');
    saveGeospatialFeature(payload, token)
      .then((resp) => {
        if (!apiOk(resp)) { setStatus('Save failed: ' + apiError(resp)); return; }
        setStatus('Updated ' + payload.featureType + ' #' + selected.id + '.');
        loadFeatures();
      })
      .catch((e) => setStatus('Save error: ' + e.message));
  }

  function deleteSelected() {
    if (!selected) return;
    const token = getAccessToken();
    setStatus('Deleting…');
    deleteGeospatialFeature(AIRPORT.icao, selected.id, token)
      .then((resp) => {
        if (!apiOk(resp)) { setStatus('Delete failed: ' + apiError(resp)); return; }
        setStatus('Deleted feature #' + selected.id + '.');
        setSelectedId(null);
        loadFeatures();
      })
      .catch((e) => setStatus('Delete error: ' + e.message));
  }

  const drawing = mode !== 'select';
  const draftCfg = MODES[mode];

  return (
    <div className="geo-cfg">
      <div className="geo-map">
        <MapContainer center={AIRPORT.center} zoom={AIRPORT.zoom}
          style={{ height: '100%', width: '100%' }}>
          <TileLayer
            attribution="&copy; OpenStreetMap contributors"
            url="https://tile.openstreetmap.org/{z}/{x}/{y}.png"
          />
          <DrawHandler active={drawing} onPoint={addPoint} />
          <MapFocus target={focusTarget} />

          {features.map((f) => {
            if (!f.parsed) return null;
            const isSel = f.id === selectedId;
            const color = TYPE_COLOR[f.featureType] || '#888';
            const handlers = { click: () => { setSelectedId(f.id); setMode('select'); } };
            if (f.parsed.kind === 'Point') {
              return (
                <CircleMarker key={f.id} center={f.parsed.latlngs}
                  radius={isSel ? 9 : 6}
                  pathOptions={{ color, fillColor: color, fillOpacity: 0.85,
                    weight: isSel ? 3 : 1 }}
                  eventHandlers={handlers}>
                  <Tooltip>{f.featureCode} {f.standType || ''}</Tooltip>
                </CircleMarker>
              );
            }
            if (f.parsed.kind === 'LineString') {
              return (
                <Polyline key={f.id} positions={f.parsed.latlngs}
                  pathOptions={{ color, weight: isSel ? 6 : 3 }}
                  eventHandlers={handlers}>
                  <Tooltip>{f.featureCode}</Tooltip>
                </Polyline>
              );
            }
            return (
              <Polygon key={f.id} positions={f.parsed.latlngs}
                pathOptions={{ color, weight: isSel ? 4 : 1,
                  fillOpacity: isSel ? 0.35 : 0.15 }}
                eventHandlers={handlers}>
                <Tooltip>{f.featureCode}</Tooltip>
              </Polygon>
            );
          })}

          {draft.length > 1 && draftCfg && draftCfg.geom !== 'Point' && (
            <Polyline positions={draft}
              pathOptions={{ color: '#FF6B35', weight: 3, dashArray: '6' }} />
          )}
          {draft.map((p, i) => (
            <CircleMarker key={'draft-' + i} center={p} radius={5}
              pathOptions={{ color: '#FF6B35', fillColor: '#fff', fillOpacity: 1, weight: 2 }} />
          ))}
        </MapContainer>
      </div>

      <div className="geo-panel">
        <h2 className="geo-title">Geospatial Configurator</h2>
        <div className="geo-subtitle">{AIRPORT.name} ({AIRPORT.icao})</div>

        <div className="geo-section">
          <div className="geo-section-label">Add new feature</div>
          <div className="geo-modes">
            {Object.keys(MODES).map((m) => (
              <button key={m}
                className={'geo-mode' + (mode === m && redrawId == null ? ' active' : '')}
                onClick={() => startAdd(m)}>+ {MODES[m].label}</button>
            ))}
          </div>
        </div>

        {drawing && (
          <div className="geo-section geo-draw">
            <div className="geo-section-label">
              {redrawId != null ? 'Move / redraw geometry' : 'New ' + draftCfg.featureType}
            </div>
            <p className="geo-hint">
              Click the map to add points ({draft.length} so far).
              {draftCfg.geom === 'Point' && ' A stand is a single dot.'}
              {draftCfg.geom === 'LineString' && ' Needs 2+ points.'}
              {draftCfg.geom === 'Polygon' && ' Needs 3+ points; the ring auto-closes.'}
            </p>
            {redrawId == null && (
              <>
                <label className="geo-field">
                  <span>Code</span>
                  <input value={formCode} placeholder="e.g. 10R/28L or 317C"
                    onChange={(e) => setFormCode(e.target.value)} />
                </label>
                {draftCfg.featureType === 'STAND' && (
                  <label className="geo-field">
                    <span>Stand type</span>
                    <select value={formStandType}
                      onChange={(e) => setFormStandType(e.target.value)}>
                      {STAND_TYPES.map((t) => <option key={t} value={t}>{t || '—'}</option>)}
                    </select>
                  </label>
                )}
                <label className="geo-field">
                  <span>Zone</span>
                  <select value={formZone} onChange={(e) => setFormZone(e.target.value)}>
                    {ZONES.map((z) => <option key={z} value={z}>{z || '—'}</option>)}
                  </select>
                </label>
              </>
            )}
            <div className="geo-btn-row">
              <button className="geo-btn primary" onClick={saveDraft}>Finish &amp; save</button>
              <button className="geo-btn" onClick={() => setDraft([])}>Clear points</button>
              <button className="geo-btn" onClick={resetDraw}>Cancel</button>
            </div>
          </div>
        )}

        {selected && !drawing && (
          <div className="geo-section geo-selected">
            <div className="geo-section-label">
              Selected — {selected.featureType} #{selected.id}
            </div>
            <label className="geo-field">
              <span>Code</span>
              <input value={editCode} onChange={(e) => setEditCode(e.target.value)} />
            </label>
            {selected.featureType === 'STAND' && (
              <label className="geo-field">
                <span>Stand type</span>
                <select value={editStandType}
                  onChange={(e) => setEditStandType(e.target.value)}>
                  {STAND_TYPES.map((t) => <option key={t} value={t}>{t || '—'}</option>)}
                </select>
              </label>
            )}
            <label className="geo-field">
              <span>Zone</span>
              <select value={editZone} onChange={(e) => setEditZone(e.target.value)}>
                {ZONES.map((z) => <option key={z} value={z}>{z || '—'}</option>)}
              </select>
            </label>
            <div className="geo-btn-row">
              <button className="geo-btn primary" onClick={saveSelectedEdits}>Save</button>
              <button className="geo-btn" onClick={startRedraw}>Move / redraw</button>
              <button className="geo-btn danger" onClick={deleteSelected}>Delete</button>
            </div>
          </div>
        )}

        <div className="geo-section">
          <div className="geo-section-label">Features {loading ? '(loading…)' : ''}</div>
          <div className="geo-filters">
            {FEATURE_TYPES.map((t) => (
              <button key={t}
                className={'geo-filter' + (listFilter === t ? ' active' : '')}
                onClick={() => setListFilter(t)}>
                <span className="geo-swatch" style={{ background: TYPE_COLOR[t] }} />
                {t}<span className="geo-count">{counts[t] || 0}</span>
              </button>
            ))}
          </div>
          <ul className="geo-list">
            {listed.map((f) => (
              <li key={f.id}
                className={'geo-list-item' + (f.id === selectedId ? ' active' : '')}
                onClick={() => { setSelectedId(f.id); setMode('select'); }}>
                <span className="geo-list-code">{f.featureCode || '(no code)'}</span>
                {f.featureType === 'STAND' && (
                  <span className="geo-list-meta">
                    {(f.standType || '?') + ' / ' + (f.zone || '?')}
                  </span>
                )}
              </li>
            ))}
            {listed.length === 0 && (
              <li className="geo-list-empty">No {listFilter} features</li>
            )}
          </ul>
          <button className="geo-btn" onClick={loadFeatures}>Reload</button>
        </div>

        {status && <div className="geo-status">{status}</div>}
      </div>
    </div>
  );
}

export default GeospatialConfigurator;
